// src/shared/ui/useDiscardConfirm.jsx
//
// Guards a form-in-a-Modal's ambient dismiss paths (backdrop click, Escape,
// the X button) behind a "Discard unsaved changes?" prompt, reusing the
// existing ConfirmModal rather than a bespoke dialog or window.confirm.
// The form's own explicit Cancel/Done button should keep calling its real
// close handler directly -- only pass `requestClose` as the Modal's
// onClose; clicking a button literally labeled Cancel is already a
// deliberate, self-explanatory action and doesn't need a second prompt.
//
// `isDirty` is the caller's own responsibility, not inferred here: each
// form-modal tracks a simple `dirty` flag, flipped to true only inside its
// own onChange/setField handlers -- never by an effect that seeds defaults
// or reseeds on reopen. That keeps this hook free of any "did the seed
// effect run yet" timing race; it only ever cares about genuine user input.
//
// Usage:
//   const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);
//   <Modal open={open} onClose={requestClose} ...>...</Modal>
//   {confirmDialog}

import { useState } from "react";
import ConfirmModal from "./ConfirmModal";

export function useDiscardConfirm(isDirty, onDiscard) {
  const [confirming, setConfirming] = useState(false);

  function requestClose() {
    if (isDirty) setConfirming(true);
    else onDiscard();
  }

  const confirmDialog = (
    <ConfirmModal
      open={confirming}
      entityLabel="unsaved changes"
      confirmLabel="Discard"
      variant="danger"
      onClose={() => setConfirming(false)}
      onConfirm={() => {
        setConfirming(false);
        onDiscard();
      }}
    >
      Closing now will lose what you&apos;ve entered.
    </ConfirmModal>
  );

  return { requestClose, confirmDialog };
}
